import ModalDefault from '../components/modal-default';
class ProductTemplate {
	constructor(el) {
		this.el = el;
		this.featuredProductEl = this.el.querySelector('.product__featured-product');
		this.featuredProductRow = this.el.querySelector('.product__featured-product-row');
		this.noResultsEl = this.el.querySelector('[data-product-no-results]');
		this.featuredId = this.featuredProductRow ? this.featuredProductRow.dataset.featuredId : null;
		this.baseUrl = window.location.origin;

		this.displayFeatured();
		this.displaySearchIcon();
		this.bindTaxonomyFilterPills();
		this.initializeModals();
	}

	/**
	 * Taxonomy filter pills on a product card: `data-{attr}` on the pill maps to
	 * the ALM taxonomy filter it should drive.
	 */
	static TAXONOMY_PILLS = [
		{ attr: 'category-filter', dataAttr: 'categoryFilter', taxonomy: 'product-category' },
	];

	/**
	 * Delegated click handler for the category pill on each product card.
	 * Clicking a pill selects the matching term in its ALM filter and dispatches
	 * a native `change` event, so it goes through the same filtering path a
	 * user picking the option by hand would use (including
	 * js/alm-filters-keyboard.js's capture-phase handler). Delegation on
	 * `document` is required because ALM replaces the listing markup on every
	 * filter request.
	 */
	bindTaxonomyFilterPills = () => {
		document.addEventListener('click', (event) => {
			for (const { attr, dataAttr, taxonomy } of ProductTemplate.TAXONOMY_PILLS) {
				const pill = event.target.closest(`[data-${attr}]`);

				if (!pill) continue;

				// Pills sit inside `.js__modal--default` on cards with no external
				// URL; without this the click would bubble up and also open the
				// product modal.
				event.stopPropagation();

				const slug = pill.dataset[dataAttr];

				if (taxonomy === 'product-category' && window.MaletisProductTypeTree && window.MaletisProductTypeTree.selectCategory(slug, pill.dataset.productType || '')) {
					this.pillScrollPending = true;
					return;
				}

				const select = this.findTaxonomyFilterSelect(taxonomy);

				// The dropdown for this taxonomy isn't configured yet, or the term
				// has no matching results and was pruned from the select. Do
				// nothing rather than guess at another control.
				if (!select || !slug) return;

				const option = Array.prototype.find.call(select.options, (opt) => opt.value === slug);

				if (!option || select.value === slug) return;

				this.pillScrollPending = true;
				select.value = slug;
				select.dispatchEvent(new Event('change', { bubbles: true }));

				return;
			}
		}, true);
	}

	/**
	 * Scroll the filter bar into view once ALM has finished rendering the
	 * results a pill click filtered to, so the user lands on the (now-changed)
	 * grid instead of staying scrolled deep in one whose contents just moved.
	 * No-ops if the bar is already on screen.
	 */
	scrollToListingTop = () => {
		const filterBar = this.el.querySelector('.banner-default__filters') || this.el.querySelector('.alm-filters');

		if (!filterBar) return;

		if (filterBar.getBoundingClientRect().top < 0) {
			filterBar.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}
	}

	/**
	 * ALM Filters doesn't put a `name` (or any taxonomy-identifying) attribute on
	 * the `<select>` itself — the taxonomy lives in a `data-taxonomy` attribute on
	 * the wrapping `.alm-filter--group` div (see ajax-load-more-filters.php,
	 * ALMFilters::alm_filters_get_container()), and that value is prefixed with an
	 * underscore (e.g. `_package-category`). Match on the stripped value so this
	 * keeps working regardless of the prefixing logic.
	 */
	findTaxonomyFilterSelect = (taxonomy) => {
		const groups = document.querySelectorAll(
			'#alm-filters-product_filters .alm-filter--group[data-fieldtype="select"]'
		);

		for (const group of groups) {
			const groupTaxonomy = (group.dataset.taxonomy || '').replace(/^_+/, '');

			if (groupTaxonomy === taxonomy) {
				return group.querySelector('select.alm-filter--item');
			}
		}

		return null;
	}

	displaySearchIcon() {
		document
			.querySelectorAll('.alm-filter--text-wrap.has-button button')
			.forEach((btn) => {
				btn.innerHTML = '<i class="fas fa-search"></i>';
			});
	}

	initializeModals() {
		this.el
			.querySelectorAll('.js__modal--default:not([data-modal-initialized])')
			.forEach((modalEl) => {
				new ModalDefault(modalEl, { modalClass: 'modal modal--product' });
				modalEl.setAttribute('data-modal-initialized', 'true');
			});
	}

	toggleNoResults(cards) {
		if (!this.noResultsEl) return;

		if (!cards.length) {
			this.noResultsEl.classList.remove('product__no-results--hidden');
			this.noResultsEl.setAttribute('aria-hidden', 'false');
		} else {
			this.noResultsEl.classList.add('product__no-results--hidden');
			this.noResultsEl.setAttribute('aria-hidden', 'true');
		}
	}

	toggleLoadMoreButton() {
		const btnWrap = this.el.querySelector('.alm-btn-wrap');
		const loadMoreBtn = this.el.querySelector('.alm-load-more-btn');

		if (!btnWrap || !loadMoreBtn) return;

		const isDone = loadMoreBtn.classList.contains('done');
		const isDisabled = loadMoreBtn.disabled;

		if (isDone || isDisabled) {
			btnWrap.classList.add('product__load-more--hidden');
		} else {
			btnWrap.classList.remove('product__load-more--hidden');
		}
	}

	displayFeatured = () => {
		window.almComplete = () => {
			this.displaySearchIcon();
			this.initializeModals();

			if (this.pillScrollPending) {
				this.pillScrollPending = false;
				this.scrollToListingTop();
			}

			const ajaxLoadMoreListing = document.querySelector('.alm-listing');

			if (!ajaxLoadMoreListing) return;

			const almFilters = ajaxLoadMoreListing.querySelector('.alm-filters');
			const cards = ajaxLoadMoreListing.querySelectorAll('.card-product');

			this.toggleNoResults(cards);
			this.toggleLoadMoreButton();

			if (!almFilters || !this.featuredProductRow) return;

			if (almFilters.dataset.url === this.baseUrl + '/products/' && cards.length) {
				this.featuredProductRow.classList.remove('hidden');
			} else {
				this.featuredProductRow.classList.add('hidden');
			}

			cards.forEach((card) => {
				if (card.dataset.postId === this.featuredId && almFilters.dataset.url === this.baseUrl + '/products/') {
					card.classList.add('hidden');
				}
			});
		};
	}
}

export default ProductTemplate;